import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check, MessageCircle, Briefcase, MapPin, DollarSign, Calendar, Send, Search, Globe, Tag, Trash2, ArrowUpRight, Loader2, Filter, RefreshCw, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useHiringManager } from '../hooks/useHiringManager';
import { useMessages } from '../hooks/useMessages';
import { useLinkedInImport } from '../hooks/useLinkedInImport';
import { useProfile } from '../hooks/useProfile';
import { useJobScraper } from '../hooks/useJobScraper';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { supabase } from '../lib/supabase';
import JobMetadataForm from '../components/JobMetadataForm';
import HiringOutcomePicker from '../components/HiringOutcomePicker';

const REJECT_REASONS = [
  'Experience level mismatch',
  'Skills not aligned',
  'Location requirements',
  'Salary expectations too high',
  'Not hiring right now',
];

const OUTCOME_LABELS = {
  strong_fit: { label: 'Strong Fit', emoji: '🌟', color: 'bg-green-100 text-green-800' },
  good_fit: { label: 'Good Fit', emoji: '👍', color: 'bg-blue-100 text-blue-800' },
  not_aligned: { label: 'Not Aligned', emoji: '↔️', color: 'bg-orange-100 text-orange-800' },
  underqualified: { label: 'Underqualified', emoji: '📊', color: 'bg-red-100 text-red-800' },
  other: { label: 'Other', emoji: '💬', color: 'bg-gray-100 text-gray-800' },
};

export default function HiringManagerView({ onStats }) {
  const { profile } = useAuth();
  const { fastTrackQueue, connectedCandidates, myJobs, availableJobs, connectWithCandidate, rejectCandidate, closeOutcome, fetchMyJobs, fetchAvailableJobs, fetchFastTrackQueue, claimJobs, unclaimMyJobs, subscribeToFastTrack, updateJobMetadata } = useHiringManager();
  const { messages, fetchMessages, sendMessage } = useMessages();
  const { saveLinkedInData, loadDemoData } = useProfile();
  const linkedIn = useLinkedInImport();
  const jobScraper = useJobScraper();

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [connectedCandidate, setConnectedCandidate] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [hmMessage, setHmMessage] = useState('');
  const [showHMMessageModal, setShowHMMessageModal] = useState(false);
  const [showCandidateMessage, setShowCandidateMessage] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [candidateMessageMap, setCandidateMessageMap] = useState({});
  const [showScanner, setShowScanner] = useState(myJobs.length === 0);
  const [scanUrl, setScanUrl] = useState('https://jobs.lever.co/nextech/');
  const [scanError, setScanError] = useState('');
  const [scanSuccess, setScanSuccess] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [editingTags, setEditingTags] = useState(null); // jobId being edited
  const [tagInput, setTagInput] = useState('');
  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [metadataTarget, setMetadataTarget] = useState(null); // { type: 'promote'|'edit', jobId, jobTitle, metadata? }
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const fileInputRef = useRef(null);

  // Combined list for the job identification modal
  const allModalJobs = [...myJobs, ...availableJobs].sort((a, b) => (a.legacy_id || 0) - (b.legacy_id || 0));

  // Push stats to header
  useEffect(() => {
    if (onStats && profile?.linkedin_connected) {
      onStats({
        role: 'hiring_manager',
        candidateCount: fastTrackQueue.length,
        jobCount: myJobs.length,
        newCandidates: fastTrackQueue.length,
      });
    }
  }, [onStats, profile?.linkedin_connected, fastTrackQueue.length, myJobs.length]);

  // Debounce filter changes for scraped jobs (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      jobScraper.fetchScrapedJobs(jobScraper.filters);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobScraper.filters]);

  // Refetch when user switches back to this PWA.
  // In demo iframes, skip polling — HM has real-time subscriptions for FastTrack
  // and polling causes scroll resets in the Job Scanner list.
  const isDemoIframe = new URLSearchParams(window.location.search).has('demo_role');
  useVisibilityRefresh(useCallback(() => {
    if (isDemoIframe) return;
    fetchMyJobs();
    fetchFastTrackQueue();
    fetchAvailableJobs();
    jobScraper.fetchScrapedJobs();
    jobScraper.fetchScrapeHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMyJobs, fetchFastTrackQueue, fetchAvailableJobs, isDemoIframe]));

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToFastTrack(() => {});
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available jobs when linkedin is connected but HM has no jobs
  useEffect(() => {
    if (profile?.linkedin_connected && myJobs.length === 0) {
      fetchAvailableJobs();
    }
  }, [profile?.linkedin_connected, myJobs.length, fetchAvailableJobs]);


  // Fetch messages when viewing connected candidate
  useEffect(() => {
    if (connectedCandidate?.job_interest?.id) {
      fetchMessages(connectedCandidate.job_interest.id);
    }
  }, [connectedCandidate, fetchMessages]);

  // Check for candidate messages across all connected candidates
  useEffect(() => {
    if (!connectedCandidates || connectedCandidates.length === 0) return;
    const jiIds = connectedCandidates.map(v => v.job_interest?.id).filter(Boolean);
    if (jiIds.length === 0) return;

    (async () => {
      const { data } = await supabase
        .from('messages')
        .select('job_interest_id, content, sender_id')
        .in('job_interest_id', jiIds)
        .order('created_at', { ascending: false });

      if (data) {
        const map = {};
        // Group by job_interest_id, keep first (most recent) candidate message per interest
        for (const msg of data) {
          const vouch = connectedCandidates.find(v => v.job_interest?.id === msg.job_interest_id);
          if (vouch && msg.sender_id === vouch.candidate?.id && !map[msg.job_interest_id]) {
            map[msg.job_interest_id] = msg;
          }
        }
        setCandidateMessageMap(map);
      }
    })();
  }, [connectedCandidates]);

  const handleStartScrape = async () => {
    if (!scanUrl.trim()) return;
    setScanError('');
    setScanSuccess('');
    const result = await jobScraper.startScrape(scanUrl.trim());
    if (result.error) {
      setScanError(result.error);
    } else {
      setScanSuccess(`Found ${result.jobsFound} job${result.jobsFound !== 1 ? 's' : ''}!`);
      setScanUrl('');
      setTimeout(() => setScanSuccess(''), 4000);
    }
  };

  const handleRescan = async (url) => {
    setScanUrl(url);
    setScanError('');
    setScanSuccess('');
    const result = await jobScraper.startScrape(url);
    if (result.error) {
      setScanError(result.error);
    } else {
      setScanSuccess(`Found ${result.jobsFound} job${result.jobsFound !== 1 ? 's' : ''}!`);
      setScanUrl('');
      setTimeout(() => setScanSuccess(''), 4000);
    }
  };

  const handleAddTag = (jobId, currentTags) => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag) return;
    if (currentTags.includes(tag)) { setTagInput(''); return; }
    jobScraper.updateTags(jobId, [...currentTags, tag]);
    setTagInput('');
  };

  const handleRemoveTag = (jobId, currentTags, tagToRemove) => {
    jobScraper.updateTags(jobId, currentTags.filter(t => t !== tagToRemove));
  };

  const handlePromoteJob = (job) => {
    setMetadataTarget({ type: 'promote', jobId: job.id, jobTitle: job.title });
    setShowMetadataForm(true);
  };

  const handleEditJobMetadata = (job) => {
    setMetadataTarget({
      type: 'edit', jobId: job.id, jobTitle: job.title,
      metadata: {
        role_family: job.role_family,
        seniority_band: job.seniority_band,
        required_skills: job.required_skills || [],
        preferred_skills: job.preferred_skills || [],
        remote_allowed: job.remote_allowed ?? true,
      },
    });
    setShowMetadataForm(true);
  };

  const handleMetadataSubmit = async (metadata) => {
    if (!metadataTarget) return;
    if (metadataTarget.type === 'promote') {
      const result = await jobScraper.promoteJob(metadataTarget.jobId, metadata);
      if (result.error) {
        setScanError(result.error);
      } else {
        fetchMyJobs();
        fetchAvailableJobs();
      }
    } else if (metadataTarget.type === 'edit') {
      await updateJobMetadata(metadataTarget.jobId, metadata);
    }
    setShowMetadataForm(false);
    setMetadataTarget(null);
  };


  const handleConnect = async () => {
    if (!selectedCandidate) return;
    await connectWithCandidate(selectedCandidate.job_interest?.id);
    // Send optional message
    if (hmMessage.trim() && selectedCandidate.candidate?.id && selectedCandidate.job_interest?.id) {
      await sendMessage(selectedCandidate.candidate.id, selectedCandidate.job_interest.id, hmMessage);
    }
    // Transition to connected state view
    setConnectedCandidate(selectedCandidate);
    setSelectedCandidate(null);
    setHmMessage('');
  };

  const handleReject = async (reason) => {
    if (!selectedCandidate) return;
    await rejectCandidate(
      selectedCandidate.job_interest?.id,
      reason,
      selectedCandidate.candidate?.id
    );
    setSelectedCandidate(null);
    setShowRejectModal(false);
  };

  const handleSendHMMessage = async () => {
    if (!hmMessage.trim() || !connectedCandidate?.candidate?.id || !connectedCandidate?.job_interest?.id) return;
    await sendMessage(connectedCandidate.candidate.id, connectedCandidate.job_interest.id, hmMessage);
    setShowHMMessageModal(false);
    setHmMessage('');
  };

  const handleImportComplete = async () => {
    await saveLinkedInData({
      profile: linkedIn.importedProfile,
      positions: linkedIn.importedPositions,
      connections: linkedIn.importedConnections,
    });
    setShowImportModal(false);
    linkedIn.reset();
    fetchMyJobs();
    fetchFastTrackQueue();
  };

  // Job identification modal handlers
  const openJobModal = () => {
    setModalSelectedIds(myJobs.map(j => j.id));
    fetchAvailableJobs();
    setShowJobModal(true);
  };

  const toggleModalJob = (jobId) => {
    setModalSelectedIds(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
    );
  };

  const handleSaveJobs = async () => {
    const currentIds = new Set(myJobs.map(j => j.id));
    const newIds = new Set(modalSelectedIds);
    const same = currentIds.size === newIds.size && [...currentIds].every(id => newIds.has(id));
    if (!same) {
      await unclaimMyJobs();
      if (modalSelectedIds.length > 0) {
        await claimJobs(modalSelectedIds);
      }
    }
    setShowJobModal(false);
  };

  // LinkedIn Import Modal
  if (showImportModal) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold">Connect Your LinkedIn</h2>
              <button onClick={() => { setShowImportModal(false); linkedIn.reset(); }}
                className="text-white hover:text-cyan-200">
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="p-4">
            {linkedIn.importStep === 1 && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-gray-900 mb-2">How to export your LinkedIn data:</h3>
                  <ol className="text-xs text-gray-700 space-y-1.5 list-decimal list-inside">
                    <li>Go to LinkedIn Settings & Privacy</li>
                    <li>Click "Get a copy of your data"</li>
                    <li>Select the data you want</li>
                    <li>Download the archive</li>
                  </ol>
                </div>
                <button onClick={() => linkedIn.setImportStep(2)}
                  className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold">
                  I Have My Data Export
                </button>
                <button onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); fetchMyJobs(); fetchFastTrackQueue(); }}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm">
                  Load Demo Data Instead
                </button>
              </div>
            )}
            {linkedIn.importStep === 2 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <div className="text-4xl mb-2">📁</div>
                  <p className="text-sm font-semibold">Upload LinkedIn Export</p>
                  <input ref={fileInputRef} type="file" accept=".zip" className="hidden"
                    onChange={(e) => e.target.files[0] && linkedIn.handleFileUpload(e.target.files[0])} />
                  <button onClick={() => fileInputRef.current?.click()}
                    className="mt-4 px-4 py-2 bg-vouch-navy text-white rounded-lg text-sm font-semibold">
                    {linkedIn.loading ? 'Processing...' : 'Choose File'}
                  </button>
                </div>
                {linkedIn.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{linkedIn.error}</div>
                )}
                <button onClick={async () => { await loadDemoData(); setShowImportModal(false); linkedIn.reset(); fetchMyJobs(); fetchFastTrackQueue(); }}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700">
                  Or load demo data instead
                </button>
              </div>
            )}
            {linkedIn.importStep === 3 && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 text-sm">Data Ready</h3>
                  {linkedIn.importedProfile && (
                    <p className="text-xs text-green-700">
                      {linkedIn.importedProfile.firstName} {linkedIn.importedProfile.lastName}
                    </p>
                  )}
                  <p className="text-xs text-green-700">{linkedIn.importedConnections.length} connections</p>
                </div>
                <button onClick={handleImportComplete}
                  className="w-full py-3 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg font-semibold">
                  Import & Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected
  if (!profile?.linkedin_connected) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-5xl mb-4">💼</div>
          <h2 className="text-xl font-bold text-vouch-navy mb-2">Find Pre-Vetted Talent</h2>
          <p className="text-gray-600 text-sm mb-6">
            Connect your LinkedIn to receive vouched candidates through your trusted network.
          </p>
          <div className="bg-gradient-to-r from-slate-50 to-cyan-50 rounded-lg p-4 mb-6 border border-cyan-100">
            <div className="text-2xl font-bold text-vouch-navy blur-sm select-none">8 candidates</div>
            <p className="text-xs text-vouch-teal mt-1 font-medium">
              Pre-vetted candidates vouched by your network
            </p>
          </div>
          <button onClick={() => setShowImportModal(true)}
            className="w-full py-3 bg-vouch-navy text-white rounded-lg font-semibold hover:bg-vouch-navy-light flex items-center justify-center gap-2">
            Connect Your Network
          </button>
          <button onClick={async () => { await loadDemoData(); fetchMyJobs(); fetchFastTrackQueue(); }}
            className="w-full mt-2 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 text-sm">
            Load Demo Data Instead
          </button>
        </div>
      </div>
    );
  }

  // No jobs yet — skip straight to main view (Job Scanner is primary action)

  // Connected state view — shown after HM connects with a candidate
  if (connectedCandidate) {
    const cand = connectedCandidate;
    // Find candidate messages (from the candidate, not from HM)
    const candidateMsg = messages.find(m => m.sender_id === cand.candidate?.id);

    return (
      <div className="max-w-lg mx-auto space-y-4">
        <button onClick={() => { setConnectedCandidate(null); setHmMessage(''); setShowCandidateMessage(false); }}
          className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to FastTrack
        </button>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="p-4 space-y-3">
            <div className="text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h3 className="font-bold text-gray-900 mb-2">Connected!</h3>
              <p className="text-sm text-gray-600 mb-3">
                You're now connected with <strong>{cand.candidate?.display_name}</strong>
              </p>
            </div>

            {/* Privacy Notice */}
            <div className="p-3 bg-green-50 border-2 border-green-300 rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-lg">🔒</span>
                <h4 className="font-bold text-green-900 text-sm">Your Info is Protected</h4>
              </div>
              <p className="text-xs text-green-800 text-center">
                The candidate doesn't see your name or contact details until you choose to share them. No spam!
              </p>
            </div>

            {/* Outcome badge (if already recorded) */}
            {cand.job_interest?.outcome && OUTCOME_LABELS[cand.job_interest.outcome] && (
              <div className={`p-3 rounded-lg border-2 ${
                cand.job_interest.outcome === 'strong_fit' || cand.job_interest.outcome === 'good_fit'
                  ? 'border-green-300 bg-green-50' : 'border-orange-200 bg-orange-50'
              }`}>
                <div className="flex items-center gap-2">
                  <ClipboardCheck size={16} className={
                    cand.job_interest.outcome === 'strong_fit' || cand.job_interest.outcome === 'good_fit'
                      ? 'text-green-700' : 'text-orange-700'
                  } />
                  <span className="font-semibold text-sm">
                    Outcome: {OUTCOME_LABELS[cand.job_interest.outcome].emoji} {OUTCOME_LABELS[cand.job_interest.outcome].label}
                  </span>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-2">
              <button className="w-full bg-gradient-to-r from-vouch-navy to-vouch-teal text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                <Calendar size={16} /> Schedule Call
              </button>
              <button
                onClick={() => { setShowHMMessageModal(true); setHmMessage(''); }}
                className="w-full border-2 border-cyan-200 text-vouch-navy py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-50"
              >
                <Send size={16} /> Send Message
              </button>
              {!cand.job_interest?.outcome && (
                <button
                  onClick={() => setShowOutcomeModal(true)}
                  className="w-full border-2 border-amber-200 text-amber-800 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-amber-50"
                >
                  <ClipboardCheck size={16} /> Close Outcome
                </button>
              )}
            </div>

            {/* Voucher thanked notice */}
            <div className="p-2 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">✉️ {cand.voucher?.display_name} has been thanked!</p>
            </div>
          </div>
        </div>

        {/* HM Message Modal */}
        {showHMMessageModal && (
          <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-vouch-teal">
            <h4 className="font-bold text-sm mb-3 text-gray-900">Send a message to {cand.candidate?.display_name}</h4>
            <p className="text-xs text-gray-600 mb-2">Introduce yourself and explain why you're interested in connecting.</p>
            <textarea
              value={hmMessage}
              onChange={(e) => setHmMessage(e.target.value)}
              placeholder="Hi! I saw your profile and I'm impressed by your experience..."
              className="w-full border-2 border-gray-300 rounded-lg p-3 text-sm mb-3 h-32 resize-none focus:border-vouch-teal focus:outline-none"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowHMMessageModal(false); setHmMessage(''); }}
                className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSendHMMessage}
                disabled={!hmMessage.trim()}
                className="flex-1 py-2 bg-vouch-navy hover:bg-vouch-navy-light text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
              >
                <Send size={14} /> Send
              </button>
            </div>
          </div>
        )}

        {/* New Message from Candidate */}
        {candidateMsg && (
          <div className="bg-cyan-100 border-2 border-vouch-teal rounded-xl overflow-hidden">
            <button
              onClick={() => setShowCandidateMessage(!showCandidateMessage)}
              className="w-full p-3 text-left hover:bg-cyan-50 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} color="#18B6B2" />
                  <span className="font-bold text-sm text-vouch-ink">New Message</span>
                </div>
                <span className="text-vouch-navy text-xs">{showCandidateMessage ? '▼' : '▶'}</span>
              </div>
              <p className="text-xs text-vouch-navy mt-1">
                {cand.candidate?.display_name} sent you an introduction
              </p>
            </button>
            {showCandidateMessage && (
              <div className="p-3 bg-white border-t-2 border-cyan-200">
                <p className="text-xs text-gray-500 mb-2">From: <strong>{candidateMsg.sender?.display_name || cand.candidate?.display_name}</strong></p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-900 italic">"{candidateMsg.content}"</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Outcome Modal */}
        {showOutcomeModal && (
          <HiringOutcomePicker
            candidateName={cand.candidate?.display_name}
            onSelect={async (outcome, note) => {
              await closeOutcome(cand.job_interest?.id, outcome, note);
              setShowOutcomeModal(false);
              // Update local state to show badge immediately
              setConnectedCandidate(prev => ({
                ...prev,
                job_interest: { ...prev.job_interest, outcome, outcome_at: new Date().toISOString() },
              }));
            }}
            onCancel={() => setShowOutcomeModal(false)}
          />
        )}
      </div>
    );
  }

  // Candidate detail view (reviewing before connect/pass)
  if (selectedCandidate) {
    const cand = selectedCandidate;
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <button onClick={() => { setSelectedCandidate(null); setHmMessage(''); }}
          className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to FastTrack
        </button>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-5 h-5 brightness-0 invert" />
              <span className="font-bold text-sm">FastTrack Candidate</span>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {/* Candidate info */}
            <div className="flex items-center gap-3">
              {cand.candidate?.photo_url ? (
                <img src={cand.candidate.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-vouch-teal" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy font-bold text-lg">
                  {cand.candidate?.display_name?.[0]}
                </div>
              )}
              <div>
                <p className="font-bold text-gray-900">{cand.candidate?.display_name}</p>
                <p className="text-sm text-gray-600">{cand.candidate?.current_title}</p>
                <p className="text-xs text-gray-500">{cand.candidate?.current_company} - {cand.candidate?.location}</p>
              </div>
            </div>

            {/* Job they're interested in */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Interested in:</p>
              <div className="flex items-center gap-2">
                <span>{cand.jobs?.logo}</span>
                <div>
                  <p className="font-semibold text-sm">{cand.jobs?.title}</p>
                  <p className="text-xs text-gray-500">{cand.jobs?.company}</p>
                </div>
              </div>
            </div>

            {/* Voucher context */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-5 h-5" />
                <span className="font-semibold text-sm text-green-800">Vouched by {cand.voucher?.display_name}</span>
              </div>
              <p className="text-sm text-green-700 italic">"{cand.note}"</p>
              <p className="text-xs text-green-600 mt-2">
                {cand.voucher?.current_company && `${cand.voucher.current_company}`}
              </p>
            </div>

            {/* Message (optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <MessageCircle size={14} className="inline mr-1" />
                Send a message (optional):
              </label>
              <textarea
                value={hmMessage}
                onChange={(e) => setHmMessage(e.target.value)}
                placeholder="I'd love to chat about this role..."
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm h-20 resize-none focus:border-vouch-teal focus:outline-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(true)}
                className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-gray-200">
                <X size={16} /> Pass
              </button>
              <button onClick={handleConnect}
                className="flex-1 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                <Check size={16} /> Connect
              </button>
            </div>
          </div>
        </div>

        {/* Reject modal */}
        {showRejectModal && (
          <div className="bg-white rounded-xl shadow p-4">
            <h4 className="font-semibold text-sm mb-3">Why isn't this a fit?</h4>
            {REJECT_REASONS.map(reason => (
              <button key={reason} onClick={() => handleReject(reason)}
                className="w-full text-left py-2 px-3 text-sm hover:bg-gray-50 rounded-lg border mb-1 hover:border-vouch-teal">
                {reason}
              </button>
            ))}
            <button onClick={() => setShowRejectModal(false)}
              className="w-full mt-2 py-2 text-gray-500 text-sm">Cancel</button>
          </div>
        )}
      </div>
    );
  }

  // Main HM view
  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Stats bar */}
      {profile?.linkedin_connected && (
        <div className="bg-vouch-teal rounded-xl px-4 py-2.5 text-white">
          <p className="text-xs text-teal-100">
            <strong className="text-white">{fastTrackQueue.length} candidates</strong> your network knows personally
          </p>
          {fastTrackQueue.length > 0 && (
            <p className="text-xs text-yellow-300 font-semibold mt-0.5">
              ✨ {fastTrackQueue.length} new {fastTrackQueue.length === 1 ? 'candidate' : 'candidates'}!
            </p>
          )}
          {myJobs.length > 0 && (
            <div className="mt-1.5 space-y-1">
              <button
                onClick={openJobModal}
                className="w-full bg-white bg-opacity-10 rounded px-2 py-1 text-left hover:bg-opacity-20 transition-all"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs text-teal-100">
                    <Briefcase size={12} className="inline mr-1" />
                    <strong className="text-white">{myJobs.length} jobs</strong> you're hiring for
                  </p>
                  <span className="text-teal-200 text-xs">Edit →</span>
                </div>
              </button>
              {myJobs.map(job => (
                <div key={job.id} className="flex items-center justify-between bg-white bg-opacity-10 rounded px-2 py-1">
                  <p className="text-xs text-teal-100 truncate flex-1">{job.title}</p>
                  <button
                    onClick={() => handleEditJobMetadata(job)}
                    className="text-[10px] text-teal-200 hover:text-white ml-2 whitespace-nowrap"
                  >
                    Edit Details
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FastTrack queue */}
      {fastTrackQueue.length > 0 ? (
        <>
          <div className="bg-gradient-to-r from-green-50 to-cyan-50 border border-green-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-5 h-5" />
              <span className="font-bold text-sm text-green-800">FastTrack Candidates</span>
            </div>
            <p className="text-xs text-green-600">
              {fastTrackQueue.length} pre-vetted {fastTrackQueue.length === 1 ? 'candidate' : 'candidates'} vouched by your network
            </p>
          </div>

          {fastTrackQueue.map(vouch => (
            <div key={vouch.id} className="bg-white rounded-xl shadow overflow-hidden">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  {vouch.candidate?.photo_url ? (
                    <img src={vouch.candidate.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-vouch-teal" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-vouch-teal bg-opacity-20 flex items-center justify-center text-vouch-navy font-bold">
                      {vouch.candidate?.display_name?.[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold">{vouch.candidate?.display_name}</p>
                    <p className="text-xs text-gray-600">{vouch.candidate?.current_title}</p>
                    <p className="text-xs text-gray-500">{vouch.candidate?.current_company}</p>
                  </div>
                </div>

                {/* Job + vouch info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-500 mb-1">For: {vouch.jobs?.title}</p>
                  <div className="flex items-center gap-1 text-xs text-green-700">
                    <img src="/brand/vouch/assets/vector/vouch-icon.svg" alt="" className="w-4 h-4" />
                    Vouched by {vouch.voucher?.display_name}
                  </div>
                </div>

                <button onClick={() => setSelectedCandidate(vouch)}
                  className="w-full py-2 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg text-sm font-semibold">
                  Review Candidate
                </button>
              </div>
            </div>
          ))}
        </>
      ) : myJobs.length > 0 ? (
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-4xl mb-3">💼</div>
          <h3 className="font-bold text-gray-900 mb-2">No FastTrack Candidates Yet</h3>
          <p className="text-sm text-gray-600">
            When candidates are vouched for your open positions, they'll appear here as FastTrack candidates.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow text-center">
          <div className="text-4xl mb-3">💼</div>
          <h3 className="font-bold text-gray-900 mb-2">No Open Positions</h3>
          <p className="text-sm text-gray-600">
            Use the Job Scanner below to scan your company's careers page and add positions you're hiring for.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Once you have jobs, vouched candidates will appear here as FastTrack candidates.
          </p>
        </div>
      )}

      {/* Connected candidates */}
      {connectedCandidates && connectedCandidates.length > 0 && (
        <>
          <div className="bg-gradient-to-r from-cyan-50 to-green-50 border border-cyan-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Check size={16} className="text-vouch-teal" />
              <span className="font-bold text-sm text-vouch-navy">Connected</span>
            </div>
            <p className="text-xs text-gray-600">
              {connectedCandidates.length} {connectedCandidates.length === 1 ? 'candidate' : 'candidates'} you've connected with
            </p>
          </div>

          {connectedCandidates.map(vouch => {
            const hasMessage = !!candidateMessageMap[vouch.job_interest?.id];
            return (
              <button
                key={vouch.id}
                onClick={() => { setConnectedCandidate(vouch); setShowCandidateMessage(false); }}
                className="w-full bg-white rounded-xl shadow overflow-hidden text-left hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    {vouch.candidate?.photo_url ? (
                      <img src={vouch.candidate.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-green-400" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-vouch-navy font-bold">
                        {vouch.candidate?.display_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold">{vouch.candidate?.display_name}</p>
                      <p className="text-xs text-gray-600">{vouch.candidate?.current_title}</p>
                      <p className="text-xs text-gray-500">{vouch.candidate?.current_company}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Connected</span>
                      {vouch.job_interest?.outcome && OUTCOME_LABELS[vouch.job_interest.outcome] && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${OUTCOME_LABELS[vouch.job_interest.outcome].color}`}>
                          {OUTCOME_LABELS[vouch.job_interest.outcome].emoji} {OUTCOME_LABELS[vouch.job_interest.outcome].label}
                        </span>
                      )}
                      {hasMessage && (
                        <span className="text-xs bg-cyan-100 text-vouch-teal px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <MessageCircle size={10} /> New Message
                        </span>
                      )}
                      {vouch.job_interest?.connected_at && !vouch.job_interest?.candidate_first_response_at &&
                        (new Date() - new Date(vouch.job_interest.connected_at)) > 48 * 60 * 60 * 1000 && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                          <AlertTriangle size={10} /> No response (48h+)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500">For: {vouch.jobs?.title}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </>
      )}

      {/* Job Scanner Section */}
      {profile?.linkedin_connected && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <button
            onClick={() => setShowScanner(!showScanner)}
            aria-expanded={showScanner}
            aria-controls="scanner-section"
            className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-vouch-navy to-vouch-teal flex items-center justify-center">
                <Globe size={20} className="text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900 text-sm">Job Scanner</p>
                <p className="text-xs text-gray-500">
                  {jobScraper.scrapedJobs.length > 0
                    ? `${jobScraper.totalCount} scraped job${jobScraper.totalCount !== 1 ? 's' : ''}`
                    : 'Scan careers pages to find jobs'}
                </p>
              </div>
            </div>
            <span className="text-gray-400 text-sm">{showScanner ? '▼' : '▶'}</span>
          </button>

          {showScanner && (
            <div id="scanner-section" className="border-t border-gray-100 p-4 space-y-4">
              {/* URL Input */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Careers Page URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={scanUrl}
                    onChange={(e) => { setScanUrl(e.target.value); setScanError(''); }}
                    placeholder="https://company.com/careers"
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-vouch-teal focus:outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && handleStartScrape()}
                    disabled={jobScraper.scraping}
                  />
                  <button
                    onClick={handleStartScrape}
                    disabled={jobScraper.scraping || !scanUrl.trim()}
                    className="px-4 py-2 bg-vouch-navy text-white rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {jobScraper.scraping ? (
                      <><Loader2 size={14} className="animate-spin" /> Scanning...</>
                    ) : (
                      <><Search size={14} /> Scan</>
                    )}
                  </button>
                </div>
                {scanError && (
                  <p className="mt-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{scanError}</p>
                )}
                {scanSuccess && (
                  <p className="mt-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{scanSuccess}</p>
                )}
              </div>

              {/* Filters */}
              {jobScraper.scrapedJobs.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    <Filter size={12} />
                    {showFilters ? 'Hide Filters' : 'Filter Jobs'}
                    {(jobScraper.filters.department || jobScraper.filters.location || jobScraper.filters.keyword) && (
                      <span className="bg-vouch-teal text-white px-1.5 py-0.5 rounded-full text-[10px]">Active</span>
                    )}
                  </button>
                  {showFilters && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={jobScraper.filters.department}
                        onChange={(e) => jobScraper.setFilters({ ...jobScraper.filters, department: e.target.value })}
                        placeholder="Department"
                        className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none"
                      />
                      <input
                        type="text"
                        value={jobScraper.filters.location}
                        onChange={(e) => jobScraper.setFilters({ ...jobScraper.filters, location: e.target.value })}
                        placeholder="Location"
                        className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none"
                      />
                      <input
                        type="text"
                        value={jobScraper.filters.keyword}
                        onChange={(e) => jobScraper.setFilters({ ...jobScraper.filters, keyword: e.target.value })}
                        placeholder="Keyword"
                        className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-vouch-teal focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Scraped Jobs List */}
              {jobScraper.loading ? (
                <div className="text-center py-4">
                  <Loader2 size={20} className="animate-spin text-vouch-teal mx-auto" />
                  <p className="text-xs text-gray-500 mt-2">Loading jobs...</p>
                </div>
              ) : jobScraper.scrapedJobs.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-medium">
                      {jobScraper.totalCount} job{jobScraper.totalCount !== 1 ? 's' : ''} found
                    </p>
                  </div>

                  {jobScraper.scrapedJobs.map(job => (
                    <div key={job.id} className={`border-2 rounded-xl p-3 transition-all ${
                      job.promoted_job_id ? 'border-green-200 bg-green-50' : 'border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm text-gray-900 truncate">{job.title}</p>
                            {job.promoted_job_id && (
                              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                                Promoted
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {job.department && (
                              <span className="flex items-center gap-1">
                                <Briefcase size={10} /> {job.department}
                              </span>
                            )}
                            {job.location && (
                              <span className="flex items-center gap-1">
                                <MapPin size={10} /> {job.location}
                              </span>
                            )}
                          </div>
                          {job.description_snippet && (
                            <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{job.description_snippet}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {job.original_url && (
                            <a
                              href={job.original_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-400 hover:text-vouch-teal rounded-lg hover:bg-gray-100"
                              aria-label="View original posting"
                            >
                              <ArrowUpRight size={14} />
                            </a>
                          )}
                          {!job.promoted_job_id && (
                            <button
                              onClick={() => jobScraper.deleteScrapedJob(job.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"
                              aria-label="Remove scraped job"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="mt-2 flex items-center flex-wrap gap-1.5">
                        {(job.tags || []).map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-[11px] bg-vouch-navy bg-opacity-10 text-vouch-navy px-2 py-0.5 rounded-full font-medium"
                          >
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(job.id, job.tags, tag)}
                              className="hover:text-red-500"
                              aria-label={`Remove tag ${tag}`}
                            >
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                        {editingTags === job.id ? (
                          <form
                            onSubmit={(e) => { e.preventDefault(); handleAddTag(job.id, job.tags || []); }}
                            className="inline-flex"
                          >
                            <input
                              type="text"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              placeholder="Add tag..."
                              className="w-20 px-2 py-0.5 text-[11px] border border-gray-300 rounded-full focus:border-vouch-teal focus:outline-none"
                              autoFocus
                              onBlur={() => { if (!tagInput.trim()) setEditingTags(null); }}
                              onKeyDown={(e) => { if (e.key === 'Escape') { setEditingTags(null); setTagInput(''); } }}
                            />
                          </form>
                        ) : (
                          <button
                            onClick={() => { setEditingTags(job.id); setTagInput(''); }}
                            className="inline-flex items-center gap-0.5 text-[11px] text-gray-400 hover:text-vouch-teal px-1.5 py-0.5 rounded-full border border-dashed border-gray-300 hover:border-vouch-teal"
                          >
                            <Tag size={10} /> tag
                          </button>
                        )}
                      </div>

                      {/* Promote button */}
                      {!job.promoted_job_id && (
                        <button
                          onClick={() => handlePromoteJob(job)}
                          className="mt-2 w-full py-1.5 bg-gradient-to-r from-vouch-navy to-vouch-teal text-white rounded-lg text-xs font-semibold"
                        >
                          Promote to My Jobs
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : jobScraper.scrapeHistory.length > 0 ? (
                <div className="text-center py-3">
                  <p className="text-xs text-gray-500">
                    No jobs match your filters. Try adjusting your search.
                  </p>
                </div>
              ) : null}

              {/* Scrape History */}
              {jobScraper.scrapeHistory.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Scan History</p>
                  <div className="space-y-1.5">
                    {jobScraper.scrapeHistory.map(session => (
                      <div key={session.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 truncate">{session.careers_url}</p>
                          <p className="text-[10px] text-gray-400">
                            {session.jobs_found} job{session.jobs_found !== 1 ? 's' : ''} found
                            {session.status === 'failed' && (
                              <span className="text-red-500 ml-1">Failed</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleRescan(session.careers_url)}
                            disabled={jobScraper.scraping}
                            className="p-1 text-gray-400 hover:text-vouch-teal disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Rescan this URL"
                            title="Rescan for new jobs"
                          >
                            <RefreshCw size={12} className={jobScraper.scraping ? 'animate-spin' : ''} />
                          </button>
                          <button
                            onClick={() => jobScraper.deleteSession(session.id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                            aria-label="Remove scan history"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Job metadata form modal */}
      {showMetadataForm && metadataTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md max-h-[80vh] overflow-y-auto">
            <JobMetadataForm
              jobTitle={metadataTarget.jobTitle}
              initialMetadata={metadataTarget.metadata}
              onSubmit={handleMetadataSubmit}
              onCancel={() => { setShowMetadataForm(false); setMetadataTarget(null); }}
            />
          </div>
        </div>
      )}

      {/* Job identification modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-vouch-navy to-vouch-teal p-4">
              <div className="flex items-center justify-center gap-2">
                <Briefcase size={24} className="text-white" />
                <h2 className="text-white font-bold text-lg">Identify Your Jobs</h2>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Select the jobs you're hiring for.</strong> Candidates see these jobs but your identity remains protected.
                </p>
              </div>
              <div className="space-y-2">
                {allModalJobs.map(job => (
                  <label
                    key={job.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      modalSelectedIds.includes(job.id)
                        ? 'border-vouch-teal bg-slate-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={modalSelectedIds.includes(job.id)}
                      onChange={() => toggleModalJob(job.id)}
                      className="mt-1 w-5 h-5 rounded"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{job.title}</div>
                      <div className="text-sm text-gray-600">{job.department}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <MapPin size={12} />
                        <span>{job.location}</span>
                        <span>-</span>
                        <DollarSign size={12} />
                        <span>{job.salary_range}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-800">
                  <span className="text-lg">🔒</span>
                  <p className="text-sm">
                    <strong>Privacy protected:</strong> Candidates see the job postings but not your name until you choose to connect.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowJobModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold">
                  Cancel
                </button>
                <button onClick={handleSaveJobs}
                  disabled={modalSelectedIds.length === 0}
                  className="flex-1 py-2.5 bg-vouch-navy text-white rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                  Save My Jobs ({modalSelectedIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
